import { useState, type ChangeEvent } from 'react';
import { Alert, Box, Button, Card, CardContent, Divider, Stack, TextField, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { useAuth } from '@/auth/AuthProvider';
import { languageLabels, type Language, useI18n } from '@/i18n';
import {
  executeVariboxImport,
  type VariboxImportReport,
  validateVariboxImport,
} from '@/services/variboxImportApi';

type SettingsState = {
  workspaceName: string;
  timezone: string;
  allowInvites: boolean;
  requireApproval: boolean;
  maintenanceReminders: boolean;
  transferNotifications: boolean;
  executiveDigest: boolean;
  sessionTimeoutWarnings: boolean;
  retentionPeriod: string;
  exportWindow: string;
};

const initialState: SettingsState = {
  workspaceName: 'Varibox Control Center',
  timezone: 'UTC',
  allowInvites: true,
  requireApproval: true,
  maintenanceReminders: true,
  transferNotifications: true,
  executiveDigest: false,
  sessionTimeoutWarnings: false,
  retentionPeriod: '365 days',
  exportWindow: '90 days',
};

function toImportErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }
  return fallback;
}

function SettingToggle({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (nextValue: boolean) => void;
}) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 1.5,
        py: 1.25,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'rgba(148,163,184,0.18)',
        backgroundColor: 'rgba(255,255,255,0.7)',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        style={{ width: 18, height: 18, accentColor: '#143f6b' }}
      />
    </Box>
  );
}

export default function SettingsPage() {
  const { t, language, setLanguage } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [settings, setSettings] = useState<SettingsState>(initialState);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationReport, setValidationReport] = useState<VariboxImportReport | null>(null);
  const [importReport, setImportReport] = useState<VariboxImportReport | null>(null);
  const [importError, setImportError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const update = <K extends keyof SettingsState>(key: K, value: SettingsState[K]) => {
    setSettings((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const languageButtons: Array<{ value: Language; label: string }> = [
    { value: 'pl', label: languageLabels.pl },
    { value: 'en', label: languageLabels.en },
  ];

  const onSelectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setValidationReport(null);
    setImportReport(null);
    setImportError('');
  };

  const onValidate = async () => {
    if (!selectedFile) {
      return;
    }

    setIsValidating(true);
    setImportError('');
    setImportReport(null);

    try {
      const report = await validateVariboxImport(selectedFile);
      setValidationReport(report);
    } catch (error) {
      setImportError(toImportErrorMessage(error, t('settings.import.uploadFailed')));
      setValidationReport(null);
    } finally {
      setIsValidating(false);
    }
  };

  const onImport = async () => {
    if (!selectedFile) {
      return;
    }

    setIsImporting(true);
    setImportError('');

    try {
      const report = await executeVariboxImport(selectedFile);
      setImportReport(report);
    } catch (error) {
      setImportError(toImportErrorMessage(error, t('settings.import.uploadFailed')));
      setImportReport(null);
    } finally {
      setIsImporting(false);
    }
  };

  const canImport = Boolean(validationReport?.validation_passed);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.4 }}>
          {t('settings.eyebrow')}
        </Typography>
        <Typography variant="h4">{t('settings.title')}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 860 }}>
          {t('settings.description')}
        </Typography>
      </Stack>

      <Alert severity="info" variant="outlined">
        {t('settings.info')}
      </Alert>

      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">{t('settings.general')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.generalDescription')}
                </Typography>
              </Box>

              <TextField
                label={t('settings.workspaceName')}
                value={settings.workspaceName}
                onChange={(event) => update('workspaceName', event.target.value)}
                fullWidth
              />

              <TextField
                label={t('settings.timezone')}
                value={settings.timezone}
                onChange={(event) => update('timezone', event.target.value)}
                helperText={t('settings.helperTexts.timezone')}
                fullWidth
              />

              <SettingToggle
                checked={settings.allowInvites}
                label={t('settings.allowInvites')}
                onChange={(nextValue) => update('allowInvites', nextValue)}
              />

              <SettingToggle
                checked={settings.requireApproval}
                label={t('settings.requireApproval')}
                onChange={(nextValue) => update('requireApproval', nextValue)}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">{t('settings.language')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.languageDescription')}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                {languageButtons.map((option) => (
                  <Button
                    key={option.value}
                    variant={language === option.value ? 'contained' : 'outlined'}
                    onClick={() => setLanguage(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </Stack>

              <Box sx={{ p: 2.5, borderRadius: 3, backgroundColor: 'rgba(20,63,107,0.06)' }}>
                <Typography variant="overline" color="text.secondary">
                  {t('settings.currentLanguage')}
                </Typography>
                <Typography variant="h6">{languageLabels[language]}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.helperTexts.currentLanguage')}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">{t('settings.appearance')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.appearanceDescription')}
                </Typography>
              </Box>

              <Stack spacing={1.25}>
                <SettingToggle checked label={t('settings.appearanceOptions.compactTableDensity')} onChange={() => undefined} />
                <SettingToggle checked label={t('settings.appearanceOptions.stickyHeader')} onChange={() => undefined} />
                <SettingToggle checked={false} label={t('settings.appearanceOptions.highContrastAccents')} onChange={() => undefined} />
              </Stack>

              <Divider />

              <Typography variant="body2" color="text.secondary">
                {t('settings.helperTexts.appearance')}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">{t('settings.notifications')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.notificationsDescription')}
                </Typography>
              </Box>

              <Stack spacing={1.25}>
                <SettingToggle
                  checked={settings.maintenanceReminders}
                  label={t('settings.maintenanceReminders')}
                  onChange={(nextValue) => update('maintenanceReminders', nextValue)}
                />
                <SettingToggle
                  checked={settings.transferNotifications}
                  label={t('settings.transferNotifications')}
                  onChange={(nextValue) => update('transferNotifications', nextValue)}
                />
                <SettingToggle
                  checked={settings.executiveDigest}
                  label={t('settings.executiveDigest')}
                  onChange={(nextValue) => update('executiveDigest', nextValue)}
                />
                <SettingToggle
                  checked={settings.sessionTimeoutWarnings}
                  label={t('settings.sessionTimeoutWarnings')}
                  onChange={(nextValue) => update('sessionTimeoutWarnings', nextValue)}
                />
              </Stack>

              <Divider />

              <Typography variant="body2" color="text.secondary">
                {t('settings.helperTexts.notifications')}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">{t('settings.import.title')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.import.description')}
                </Typography>
              </Box>

              {isAdmin ? (
                <>
                  <Stack spacing={1.5}>
                    <Button variant="outlined" component="label">
                      {selectedFile ? selectedFile.name : t('settings.import.chooseFile')}
                      <input hidden type="file" accept=".xlsx" onChange={onSelectFile} />
                    </Button>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button variant="outlined" onClick={onValidate} disabled={!selectedFile || isValidating || isImporting}>
                        {isValidating ? t('settings.import.validating') : t('settings.import.validateButton')}
                      </Button>
                      <Button variant="contained" onClick={onImport} disabled={!selectedFile || !canImport || isValidating || isImporting}>
                        {isImporting ? t('settings.import.importing') : t('settings.import.importButton')}
                      </Button>
                    </Stack>
                  </Stack>

                  {importError ? <Alert severity="error">{importError}</Alert> : null}

                  {validationReport ? (
                <Alert severity={validationReport.validation_passed ? 'success' : 'warning'}>
                  {validationReport.validation_passed
                    ? t('settings.import.validationPassed')
                    : t('settings.import.validationFailed')}
                </Alert>
                  ) : null}

                  {validationReport ? (
                <Stack spacing={0.75}>
                  <Typography variant="subtitle2">{t('settings.import.reportTitle')}</Typography>
                  <Typography variant="body2">{t('settings.import.totalRows')}: {validationReport.total_rows}</Typography>
                  <Typography variant="body2">{t('settings.import.importableRows')}: {validationReport.importable_rows}</Typography>
                  <Typography variant="body2">{t('settings.import.skippedExisting')}: {validationReport.skipped_existing}</Typography>
                  <Typography variant="body2">{t('settings.import.pl01Count')}: {validationReport.pl01_count}</Typography>
                  <Typography variant="body2">{t('settings.import.pl99Count')}: {validationReport.pl99_count}</Typography>
                  {validationReport.errors.length > 0 ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('settings.import.errors')}:</Typography>
                      {validationReport.errors.map((item, index) => (
                        <Typography key={`${item.row}-${item.column}-${index}`} variant="caption" display="block" color="error.main">
                          {t('settings.import.row')} {item.row} | {item.column} | {item.value ?? '-'} | {item.reason}
                        </Typography>
                      ))}
                    </Box>
                  ) : null}
                </Stack>
                  ) : null}

                  {importReport ? (
                <Stack spacing={0.75}>
                  <Alert severity="success">{t('settings.import.importFinished')}</Alert>
                  <Typography variant="body2">{t('settings.import.imported')}: {importReport.imported}</Typography>
                  <Typography variant="body2">{t('settings.import.skippedExisting')}: {importReport.skipped_existing}</Typography>
                  <Typography variant="body2">{t('settings.import.clientsCreated')}: {importReport.clients_created}</Typography>
                  <Typography variant="body2">{t('settings.import.clientsReused')}: {importReport.existing_clients_reused}</Typography>
                </Stack>
                  ) : null}
                </>
              ) : (
                <Alert severity="info">Import plików Variboxów jest dostępny wyłącznie dla administratora.</Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">{t('settings.applicationInfo')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.appInfoDescription')}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                <Box sx={{ p: 2, borderRadius: 3, backgroundColor: 'rgba(20,63,107,0.06)' }}>
                  <Typography variant="overline" color="text.secondary">
                    {t('settings.appName')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Varibox Control Center
                  </Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: 3, backgroundColor: 'rgba(20,63,107,0.06)' }}>
                  <Typography variant="overline" color="text.secondary">
                    {t('settings.appVersion')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t('settings.versionValue')}
                  </Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: 3, backgroundColor: 'rgba(15,118,110,0.06)' }}>
                  <Typography variant="overline" color="text.secondary">
                    {t('settings.architecture')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t('settings.architectureValue')}
                  </Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: 3, backgroundColor: 'rgba(15,118,110,0.06)' }}>
                  <Typography variant="overline" color="text.secondary">
                    {t('settings.stack')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t('settings.stackValue')}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('settings.retentionPeriod')}
                  value={settings.retentionPeriod}
                  onChange={(event) => update('retentionPeriod', event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t('settings.exportWindow')}
                  value={settings.exportWindow}
                  onChange={(event) => update('exportWindow', event.target.value)}
                  fullWidth
                />
              </Stack>

              <Button variant="contained">{t('settings.saveSettings')}</Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}